import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { RadioGroup } from '@material-ui/core';
import { createStructuredSelector } from 'reselect';
import { ValidatorForm } from 'react-material-ui-form-validator';
import HiddenContent from 'components/Ui/ScreenReader';
import { Button, Input, Select, Radio, Div, Checkbox } from 'components/Ui';
import { CANADA_REGIONS, US_REGIONS } from 'utils/selectOptions';
import * as actions from 'containers/PrimoProfile/actions';
import {
  selectIsProcessing,
  selectPayMyBillError,
} from 'containers/PrimoProfile/selectors';
import { selectCountryCode } from 'containers/PrimoAccount/selectors';
import commonStyles from 'containers/PrimoProfile/styles.scss';
import { selectUserEmail } from 'containers/Authentication/selectors';
import { isCanadianBrand } from 'utils/translation';

import styles from 'containers/PrimoProfile/BillingInformation/styles.scss';

const EditBankAccountForm = ({
  defaultValues = {
    accountType: 'checking',
    autopay: false,
  },
  isNew = false,
  isDefaultPaymentMethod = false,
  isParentSubmit = false,
  onChange,
  isProcessing,
  addBankAccount,
  updateBankAccount,
  clientLocation = 'US',
  hide: hideDrawer,
  saveForFuture = true,
  email,
}) => {
  const [values, changeValues] = useState({
    ...defaultValues,
    saveForFuture: saveForFuture ? defaultValues.saveForFuture : true,
  });

  const maxLength = { routingNumber: 9, accountNumber: 17 };

  const handleChange = ({ target: { value, name } }) => {
    changeValues({ ...values, [name]: value });
    if (onChange && isParentSubmit) {
      onChange({ name, value });
    }
  };

  const handleCheckAutopay = (checked) => {
    changeValues({ ...values, autopay: checked });
  };

  const handleNumberChange = ({ target: { value, name } }) => {
    const numberValue = value.replace(/[^0-9]/g, '').slice(0, maxLength[name]);
    changeValues({ ...values, [name]: numberValue });
    if (onChange && isParentSubmit) {
      onChange({ name, numberValue });
    }
  };

  const handleSubmit = () => {
    values.email = email;

    if (isNew) {
      addBankAccount(values);
    } else {
      updateBankAccount(values);
    }
  };
  const isAutoPayAvailable = () => isCanadianBrand();
  const FormComponent = isParentSubmit ? Fragment : ValidatorForm;
  const componentProps = isParentSubmit ? {} : { onSubmit: handleSubmit };

  const clientLocationLabel = clientLocation === 'US' ? 'State' : 'Province';
  const stateIsRequiredMessage = `${clientLocationLabel} is required`;
  const headerText = `${isNew ? 'Add new bank' : 'Edit bank'} account`;

  return (
    <FormComponent {...componentProps}>
      {!isDefaultPaymentMethod && (
        <Div className={commonStyles.header}>{headerText}</Div>
      )}
      <div className={commonStyles.inputBlock}>
        <RadioGroup
          name="accountType"
          className={styles.radioGroup}
          value={values.accountType}
          onChange={handleChange}
          row
        >
          <Radio value="checking" label="Checking" disabled={!isNew} />
          <Radio value="savings" label="Savings" disabled={!isNew} />
        </RadioGroup>
      </div>
      <div className={commonStyles.inputBlock}>
        {isNew && (
          <Input
            label="Routing number"
            name="routingNumber"
            ariaLabel="Routing number"
            smallLabel
            value={values.routingNumber}
            onChange={handleNumberChange}
            validators={['required', 'routingNumber']}
            errorMessages={[
              'Routing number is required',
              'Routing number must be 9 digits',
            ]}
          />
        )}
        <Input
          label="Account number"
          name="accountNumber"
          ariaLabel="Account number"
          smallLabel
          value={!isNew ? `************${values.last4}` : values.accountNumber}
          onChange={handleNumberChange}
          readOnly={!isNew}
          validators={['required', 'bankAccountNumber']}
          errorMessages={[
            'Account number is required',
            'Account number must be > 3 digits',
          ]}
        />
      </div>
      <div className={commonStyles.inputBlock}>
        <Input
          label="Name on the Account"
          name="nameOnAccount"
          ariaLabel="Name on the account"
          smallLabel
          value={values.nameOnAccount}
          onChange={handleChange}
          validators={['required']}
          errorMessages={['Name is required']}
        />
      </div>
      <div className={commonStyles.inputBlock}>
        <Input
          label="Street address"
          name="address1"
          ariaLabel="Street address"
          smallLabel
          value={values.address1}
          onChange={handleChange}
          validators={['required']}
          errorMessages={['Street address is required']}
        />
        <Input
          label="City"
          name="city"
          ariaLabel="City"
          smallLabel
          value={values.city}
          onChange={handleChange}
          validators={['required']}
          errorMessages={['City is required']}
        />
      </div>
      <div className={commonStyles.inputBlock}>
        <Select
          label={clientLocationLabel}
          value={values.stateOrProvinceCode}
          items={clientLocation === 'US' ? US_REGIONS : CANADA_REGIONS}
          onChange={handleChange}
          name="stateOrProvinceCode"
          icon="primaryArrow"
          smallLabel
          className={styles.stateSelect}
          validators={['required']}
          errorMessages={[stateIsRequiredMessage]}
        />
        <Input
          label="ZIP code"
          name="postalCode"
          ariaLabel="ZIP code"
          smallLabel
          value={values.postalCode}
          onChange={handleChange}
          validators={['required']}
          errorMessages={['ZIP code is required']}
        />
      </div>
      {/* {saveForFuture && */}
      {/*  <div className={styles.checkbox}> */}
      {/*    <Checkbox */}
      {/*      label="Save this bank account for future payments" */}
      {/*      name="saveForFuture" */}
      {/*      isRectangle */}
      {/*      labelClassName={commonStyles.checkbox} */}
      {/*      value={values.saveForFuture} */}
      {/*      onChange={onRadioChange} */}
      {/*    /> */}
      {/*  </div> */}
      {/* } */}
      {!isParentSubmit && (
        <Fragment>
          {isAutoPayAvailable() && (
            <div className={styles.checkbox}>
              <Checkbox
                label="Make this my AutoPay method"
                name="autopay"
                isRectangle
                labelClassName={commonStyles.checkbox}
                checked={values.autopay}
                onChange={handleCheckAutopay}
              />
            </div>
          )}
          <Div
            className={styles.bottomNote}
            translationKey={'NoteBankAccountPayments'}
          >
            Note: Bank account payments can take up to one week to process and
            clear. Credit/debit card payments can take up to two days to
            process.
          </Div>
          <div className={styles.formBottom}>
            <Button
              className={commonStyles.cancelButton}
              text="Cancel"
              onClick={hideDrawer}
              type="button"
              disabled={isProcessing}
            />
            <Button
              className={commonStyles.button}
              text="Save"
              data-tracking="Save Bank Account"
              loading={isProcessing}
            />
          </div>
          <HiddenContent>
            <Input
              label="State"
              name="stateOrProvinceCode"
              ariaLabel="State"
              value={values.stateOrProvinceCode}
              onChange={handleChange}
            />
          </HiddenContent>
        </Fragment>
      )}
    </FormComponent>
  );
};

const mapStateToProps = createStructuredSelector({
  isProcessing: selectIsProcessing(),
  clientLocation: selectCountryCode(),
  payMyBillError: selectPayMyBillError(),
  email: selectUserEmail(),
});

const mapDispatchToProps = {
  addBankAccount: actions.addBankAccount,
  updateBankAccount: actions.updateBankAccount,
  resetPayMyBillError: actions.resetPayMyBillError,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditBankAccountForm);
